import window from 'window-or-global';
import { token } from '@atlaskit/tokens';
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';
import { ConfluenceDialog } from '../resources/dialog';
import { getRte } from '../../facades/editor';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

export function auiWindowManager() {
	const plugger = (Raphael) => {
		// Hard coded numbers not nice at all, but see dialog.js HEADER_HEIGHT and BUTTONS_HEIGHT :(
		const DIALOG_HEADER_SIZE = 56;
		const DIALOG_BUTTON_PANEL_SIZE = 51;
		const DIALOG_CONTENT_PADDING = 20;

		const EventDispatcher = new window.tinymce.util.Dispatcher();
		const isIE = window.tinymce.Env.ie;

		const AUIWindowManager = {
			init: (ed) => {
				// Replace window manager
				ed.on('init', () => {
					ed.windowManager = new window.tinymce.AUIWindowManager(ed);
					// DOM.loadCSS(url + '/skins/' + (ed.settings.inlinepopups_skin || 'clearlooks2') + "/window.css");
				});
			},

			getInfo: () => {
				return {
					longname: 'AUIWindowManager',
					author: 'Atlassian',
					authorurl: 'http://www.atlassian.com',
					version: `${window.tinymce.majorVersion}.${window.tinymce.minorVersion}`,
				};
			},
		};

		const WindowManager = {
			/**
			 * Create the AUIWindowManager plugin.
			 * This is very important that this is a function, not an arrow function, because the arrow function
			 * doesn't have a prototype.
			 *
			 * The code at the bottom of this file has this weird notation -- this is actually an extension. The arrow
			 * function makes it not work.
			 *
			 * Why did it work before? Because babel transpiles arrow functions to functions. FML
			 *
			 * window.tinymce.create(
			 *   "tinymce.AUIWindowManager:tinymce.WindowManager",
			 *   p.WindowManager
			 * );
			 * @constructor
			 */
			// eslint-disable-next-line object-shorthand
			AUIWindowManager: function (ed) {
				const t = this;
				t.eventDispatcher = EventDispatcher;

				t.editor = ed;
				t.params = {};
				t.features = {};
				t.modalDialogsStack = [];
				t.dialogs = {};
				t.count = 0;
			},

			/**
			 * Open and return a window as specified by the settings parameter.
			 *
			 * The created window is returned and should be supplied to the close function to dismiss it later.
			 *
			 * Note that regardless of the setting, all windows in this Window Manager are modal. Likewise, they are
			 * not resizeable and their position is centred regardless of the position requested.
			 *
			 * The following settings are supported -
			 *     name (string) - Window title resource key - will be localized.
			 *     file (string) - URL of the file to open in the window.
			 *     content (string) - HTML Content to be used. This is only applicable for non-popup windows and will be used
			 *     in preference to the file setting
			 *     width (int) - Width in pixels. (default 400)
			 *     height (int) - Height in pixels. (default 250)
			 *     popup (boolean) - defaults to true. Indicates that the window contents should be rendered in an
			 *                       iframe, for compatibility with standard TinyMCE plugins.
			 *
			 * The following custom params are supported -
			 *     cssClass (string) - a CSS class to put on the opened dialog (default is tinymce-auidialog)
			 *     helpLink (string) - the property name of a help link you want added to the dialog.
			 *     buttons (array[Object]) - an array of buttons to be added in order.
			 *                               Each item in the array should have a label String and an action function.
			 *     cancelLink (boolean) - specified whether a cancel button should be automatically added - defaults to true.
			 *
			 * @returns the id of the dialog opened.
			 */
			open(settings, params) {
				const t = this;

				settings = settings || {};
				t.initialiseSettings(settings);

				params = params || {};
				params = this.setDefaultParameters(params);
				params.mce_width = settings.width;
				params.mce_height = settings.height;
				params.mce_auto_focus = settings.auto_focus;

				if (isIE) {
					settings.center = true;
					settings.help = false;
					settings.dialogWidth = `${settings.width}px`;
					settings.dialogHeight = `${settings.height}px`;
				}

				t.features = settings;
				t.params = params;

				t.eventDispatcher.dispatch('open', { t, settings, params });

				let height = settings.height;
				if (settings.name) {
					height += DIALOG_HEADER_SIZE;
				}

				if (params.cancelLink || params.buttons) {
					height += DIALOG_BUTTON_PANEL_SIZE;
				}

				// include the padding
				height += 2 * DIALOG_CONTENT_PADDING;

				// add an arbitrary amount to cope with the user agent border that is often applied to iframes
				if (settings.popup) {
					height = height + 8;
				}

				const id = settings.id || window.tinymce.DOM.uniqueId();
				const dialog = new ConfluenceDialog().component({
					width: settings.width,
					height,
					id,
					onCancel: () => {
						dialog.hide().remove();
					},
				});
				if (settings.name) {
					dialog.addHeader(this.editor.translate(`tinymce.${settings.name}`));
				}

				this.createDialogButtons(dialog, params, id);

				settings.file = settings.file || settings.url;
				if (settings.file) {
					settings.file = settings.file;
				}

				this.createDialogContent(dialog, settings, params, id);
				params.helpLink && this.createHelpLink(dialog, params.helpLink, params.helpName, id);
				params.hintText && this.createHintText(dialog, params.hintText, id);

				// Add window so it can be managed elsewhere if necessary
				const dialogDetails = {
					id,
					settings,
					params,
					dialog,
				};

				this.modalDialogsStack.push(dialogDetails);
				this.dialogs[id] = dialogDetails;
				this.count++;

				getRte().BookmarkManager.storeBookmark();
				dialog.show();

				if (dialog.takeFocus != undefined) {
					dialog.takeFocus();
				}

				// IE 8 is getting confused with the panel size layout and always including scrollbars.
				// This fixes it.
				if (isIE) {
					$(`#${id}`).css('overflow', 'hidden');
					$(`#${id}.${params.cssClass}`).css('overflow', 'hidden');
				}

				return id;
			},

			initialiseSettings: (settings) => {
				settings.width = parseInt(settings.width || 400, 10);
				settings.height = parseInt(settings.height || 250, 10);
				settings.resizable = false;
				if (settings.popup == undefined) {
					settings.popup = true;
				}

				if (settings.popup && settings.content != undefined) {
					settings.popup = false;
				}

				if (settings.scrollbars) {
					settings.scrollbars = 'yes';
				} else {
					settings.scrollbars = 'no';
				}
			},

			/**
			 * Create any specified buttons on the dialog, as well as adding a help link if the parameter
			 * is supplied
			 */
			createDialogButtons(dialog, parameters, dialogId) {
				if (parameters.buttons) {
					for (let i = 0; i < parameters.buttons.length; i++) {
						const buttonDef = parameters.buttons[i];
						dialog.addButton(
							this.editor.translate(`tinymce.${buttonDef.label}`),
							buttonDef.action,
							buttonDef.customClass,
						);
					}
				}

				if (parameters.cancelLink) {
					dialog.addCancel(this.editor.translate('tinymce.auiwindowmanager.cancel'), () => {
						return window.tinymce.activeEditor.windowManager.close(dialogId);
					});
				}
			},

			/**
			 * Create the content of the dialog taking into account whether an iframe is required or
			 * dynamic loading into the DOM.
			 */
			createDialogContent(dialog, settings, params, id) {
				const contentId = this.createContentId(id);
				const panelId = `panel_${contentId}`;

				if (settings.popup) {
					dialog.addPanel(
						panelId,
						$(
							`<iframe id="${contentId}" width="100%" height="${settings.height}px" frameborder="0" name="auidialogiframe" src="${settings.file}" scrolling="${settings.scrollbars}"></iframe>`,
						),
						params.cssClass,
					);
					dialog.takeFocus = () => {
						$(`#${contentId}`).focus();
					};
				} else {
					if (settings.content == undefined) {
						// Dynamically loading the panel content from the settings.file
						dialog.addPanel(panelId, $(`<div id="${contentId}"/>`), params.cssClass);
						// Load the content of the window
						$(`#${contentId}`).load(settings.file);
						// TODOXHTML Add some error handling for when the content doesn't load
					} else {
						// Statically provided panel content
						dialog.addPanel(
							panelId,
							$(`<div id="${contentId}">${settings.content}</div>`),
							params.cssClass,
						);
					}

					dialog.takeFocus = () => {
						// set the focus to the first input element found
						const inputs = $(`#${contentId}:input`);
						if (inputs.length) {
							$(inputs[0]).focus();
						} else {
							// set focus to the first button found on the dialog
							const buttons = $(`#${contentId} .button-panel button`);
							if (buttons.length) {
								$(buttons[0]).focus();
							}
						}
					};
				}

				dialog.gotoPanel(0, 0);
			},

			//can be used to render help text directly instead of making a request for a link
			createHintText: (dialog, hintText, id) => {
				const tip = $('<div></div>').addClass('dialog-tip');
				tip.text(window.tinymce.activeEditor.translate(`tinymce.${hintText}`));
				$(`#${id} .dialog-button-panel`).append(tip);
			},

			/**
			 * If specified in the settings then add a help link to the button bar of the dialog.
			 */
			createHelpLink: (dialog, linkKey, linkName, id) => {
				const helpLink = $('<div></div>').addClass('dialog-help-link');
				helpLink.load(`${CONTEXT_PATH}/plugins/tinymce/helplink.action`, {
					linkUrlKey: linkKey,
					linkNameKey: linkName || '',
				});
				$(`#${id} .dialog-components .dialog-title`).prepend(helpLink);
			},

			/**
			 * Close the specified window
			 */
			close(win, id) {
				let dialogId;
				let dialog;
				if (typeof win === 'string') {
					dialogId = win;
				} else if (id) {
					dialogId = id;
				} else {
					// a window was supplied, without an id so we just assume the last opened dialog is to
					// be closed
					dialog = this.modalDialogsStack.pop();
					if (dialog) {
						dialogId = dialog.id;
					}
				}

				if (!this.dialogs[dialogId]) {
					if (process.env.NODE_ENV !== 'production') {
						console.log(`Couldn't find id ${dialogId} in dialogs array so dialog is not closed.`);
					}
				} else {
					this.count--;
					dialog = this.dialogs[dialogId];
					dialog.dialog.remove();
					this.dialogs[dialogId] = null;

					// set focus back to the editor
					window.tinymce.activeEditor.focus();

					getRte().BookmarkManager.restoreBookmark();
				}
				return false;
			},

			alert(title, callback, scope) {
				const settings = {
					content: `<p>${title}</p>`,
					width: 500,
					height: 160,
					popup: false,
				};

				let alertDialogId;

				const okCallback = function () {
					window.tinymce.activeEditor.windowManager.toggleOtherDialogs(alertDialogId, true);
					window.tinymce.activeEditor.windowManager.close(alertDialogId);
					if (callback) {
						callback.call(scope || this);
					}
				};

				const params = {
					buttons: [
						{
							label: 'auiwindowmanager.ok',
							action: okCallback,
						},
					],
					cancelLink: false,
					cssClass: 'tinymce-auidialog-alert',
				};

				alertDialogId = this.open(settings, params);

				// Disable the buttons on all the other dialogs while the alert is shown.
				this.toggleOtherDialogs(alertDialogId, false);
			},

			createInstance: (cl, a, b, c, d, e) => {
				const f = window.tinymce.resolve(cl);

				return new f(a, b, c, d, e);
			},

			// Hide the button panel on all dialogs except the alert one that was just opened.
			// The button panel may contain a cancel link so disabling is not enough. A hide is required.
			toggleOtherDialogs(alertDialogId, enable) {
				for (let i = 0; i < this.modalDialogsStack.length; i++) {
					const dialog = this.modalDialogsStack[i];
					if (dialog.id != alertDialogId) {
						$(`${dialog.id} .dialog-button-panel`).toggle(enable);
					}
				}
			},

			confirm(t, cb, s, w) {
				w = w || window;

				cb.call(s || this, w.confirm(this._decode(this.editor.translate(`tinymce.${t}`))));
			},

			/**
			 * Change the busy status of the dialog. If true then the dialog will indicate it is busy and not accept
			 * any input. If false then the dialog should revert to it's normal state.
			 *
			 *  @param dialogId The id of the dialog to change the status of
			 *  @param status true or false.
			 */
			setBusy(dialogId, busy) {
				const dialog = this.dialogs[dialogId];
				const busyId = `#${this.createBusyContentId(dialogId)}`;
				const contentId = `#${this.createContentId(dialogId)}`;

				const $content = $(contentId);
				const $busyPanel = $(busyId);

				if (!dialog) {
					return;
				}

				if (busy) {
					// hide the content panel for this dialog

					const content = $(contentId);

					content.hide();
					const contentHolder = content.parent();

					let busyPanel = $(busyId, contentHolder);
					if (!busyPanel.length) {
						contentHolder.append($(`<div id="${busyId}" class="spinner"></div>`));
						busyPanel = $(busyId, contentHolder);

						Raphael.spinner(busyPanel[0], 50, token('color.icon.accent.gray', '#666'));
					}

					$('.button-panel button', $(`#${dialogId}`)).each((index, element) => {
						$(element).prop('disabled', true);
					});

					$(busyPanel[0]).show();
				} else {
					// hide the busy panel and show the content again
					if ($busyPanel.length) {
						$busyPanel.hide();
					}

					$('.button-panel button', $(`#${dialogId}`)).each((index, element) => {
						$(element).prop('disabled', false);
					});

					$content.show();
				}
			},

			logMCESelection: (title) => {
				const s = window.tinymce.activeEditor.selection;
				if (process.env.NODE_ENV !== 'production') {
					console.log('******************************');
					console.log(`Logging TinyMCE selection title:    ${title}`);
					console.log('Bookmark:');
					console.log(s.getBookmark());
					const rangeNodeText =
						$(s.getRng().startContainer).text() || $(s.getRng().startContainer.parentNode).text();
					console.log(`Range: ${rangeNodeText}`);
					console.log(s.getRng());
				}
			},

			//
			// ---- Internal functions --------------------------------------------
			//
			setDefaultParameters: (params) => {
				params.inline = false;

				if (!params.cssClass) {
					params.cssClass = 'tinymce-auidialog';
				}

				if (params.cancelLink != false) {
					params.cancelLink = true;
				}

				return params;
			},

			/**
			 * @param id the id to create a content id for.
			 * @return a contentId for the supplied id
			 */
			createContentId: (id) => {
				return `content_${id}`;
			},

			createBusyContentId: (id) => {
				return `content_busy_${id}`;
			},

			resizeBy: () => {
				// NOOP
			},
		};

		return {
			AUIWindowManager,
			WindowManager,
		};
	};

	const p = plugger(window.Raphael);
	window.tinymce.create('tinymce.plugins.AUIWindowManager', p.AUIWindowManager);
	window.tinymce.create('tinymce.AUIWindowManager:tinymce.WindowManager', p.WindowManager);
	window.tinymce.PluginManager.add('auiwindowmanager', window.tinymce.plugins.AUIWindowManager);
}

export function auiWindowManagerWRM() {
	return ['wr!confluence.web.resources:raphael'];
}
